import {Injectable} from '@angular/core';
import {firstValueFrom, Observable} from "rxjs";
import {ApiHelperService} from '../../core/helpers/api-helper.service';
import {environment} from '../../../environments/environment';
import {PolicyType} from '../../shared/models';
import {LocaleService} from '../../core/services/locale.service';

@Injectable({
    providedIn: 'root'
})
export class DocsService {

    constructor(
        private api: ApiHelperService,
        private localeService: LocaleService,
    ) {
    }

    getListPurchaseOrder(params: any): Observable<any> {
        return this.api.getAll(environment.GET_PURCHASE_ORDER_LIST, params);
    }

    getPurchaseOrderDetail(id: any): Observable<any> {
        return this.api.getById(environment.GET_PURCHASE_ORDER_DETAIL, id);
    }

    cancelPurchaseOrder(id: any): Observable<any> {
        return this.api.updateById(environment.CANCEL_PURCHASE_ORDER, id);
    }

    getGetProfileInformation(): Observable<any> {
        return this.api.getAll(environment.GET_PROFILE_DETAIL);
    }

    async getActivePolicy(params: { type: string }): Promise<any> {
        const headers = { 'Accept-Language': this.localeService.getCurrentLocale() };
        return await firstValueFrom(this.api.getAll(environment.POLICY_GET_ACTIVE, params, headers));
    }

    async acceptPolicy(id: number | string): Promise<any> {
        return await firstValueFrom(this.api.createById(environment.POLICY_ACCEPT, String(id)));
    }

    /** Accepts the current ACTIVE version of both policy types, if any. Never throws — a missing ACTIVE version or a failed accept call is not fatal to the caller's login/redirect flow. */
    async acceptActivePolicies(): Promise<void> {
        await Promise.allSettled(
            [PolicyType.TERMS_OF_SERVICE, PolicyType.PRIVACY_POLICY].map(async (type) => {
                const res = await this.getActivePolicy({ type });
                const id = res?.data?.id;
                if (id) {
                    await this.acceptPolicy(id);
                }
            })
        );
    }

    async getPolicyAcceptances(userId: number | string): Promise<any> {
        return await firstValueFrom(this.api.getById(environment.POLICY_ACCEPTANCES, String(userId)));
    }

    /**
     * Bandaid for accept-on-every-login: BE's accept endpoint only succeeds the first time a user
     * accepts a given version — repeat calls error rather than no-op, despite the guide's step 7b
     * claiming idempotency. Should really be fixed server-side, but until then: check acceptance
     * history first, and only fall through to `acceptActivePolicies()` if at least one active
     * version isn't already accepted (e.g. accepted Terms but not Privacy Policy).
     * Response shape confirmed live 2026-07-23 (`GET /users/2/policy-acceptances`): each entry is
     * `{ id, userId, policyVersionId, type, version, title, acceptedTime, source }` — `policyVersionId`
     * is indeed the field to match against the active version ids. Any future mismatch/parse
     * failure here still fails safe: falls through to calling acceptActivePolicies() as before.
     */
    async acceptActivePoliciesIfNeeded(userId: number | string): Promise<void> {
        try {
            const [tos, privacy] = await Promise.all([
                this.getActivePolicy({ type: PolicyType.TERMS_OF_SERVICE }),
                this.getActivePolicy({ type: PolicyType.PRIVACY_POLICY }),
            ]);
            const activeIds = [tos?.data?.id, privacy?.data?.id].filter(Boolean);
            const history = await this.getPolicyAcceptances(userId);
            const acceptedIds = (history?.data ?? []).map(
                (entry: any) => entry?.policyVersionId ?? entry?.id
            );
            if (activeIds.length && activeIds.every((id) => acceptedIds.includes(id))) {
                return;
            }
        } catch {
            // History check failed for any reason — fall through to the normal accept flow.
        }
        await this.acceptActivePolicies();
    }
}
